// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import Accounts from '@app/features/auth/state/Accounts';
import {ActiveNowTabContent as ActiveNowTab} from '@app/features/user/components/modals/tabs/privacy_safety_tab/ActiveNowTab';
import {CommunicationTabContent as CommunicationTab} from '@app/features/user/components/modals/tabs/privacy_safety_tab/CommunicationTab';
import {ConnectionsTabContent as ConnectionsTab} from '@app/features/user/components/modals/tabs/privacy_safety_tab/ConnectionsTab';
import {DataDeletionTabContent as DataDeletionTab} from '@app/features/user/components/modals/tabs/privacy_safety_tab/DataDeletionTab';
import {DataExportTabContent as DataExportTab} from '@app/features/user/components/modals/tabs/privacy_safety_tab/DataExportTab';
import {ProfilePrivacyTabContent as ProfilePrivacyTab} from '@app/features/user/components/modals/tabs/privacy_safety_tab/ProfilePrivacyTab';
import {SensitiveContentTabContent as SensitiveContentTab} from '@app/features/user/components/modals/tabs/privacy_safety_tab/SensitiveContentTab';
import styles from '@app/features/user/components/modals/tabs/TabInline.module.css';
import {
	shouldShowDirectMessageSettings,
	shouldShowFriendSettings,
	shouldShowFriendsAndDirectMessageSettings,
} from '@app/features/user/components/settings_utils/SettingsVisibility';
import {getFriendsAndDirectMessagesSectionLabel} from '@app/features/user/components/settings_utils/section_registry/PrivacySafetySections';
import {useUserSettingsMutationController} from '@app/features/user/hooks/useUserSettingsMutationController';
import {
	ACTIVITY_SHARING_DESCRIPTOR,
	PROFILE_PRIVACY_LABEL_DESCRIPTOR as PROFILE_PRIVACY_DESCRIPTOR,
	SENSITIVE_CONTENT_2_DESCRIPTOR as SENSITIVE_CONTENT_DESCRIPTOR,
} from '@app/features/user/utils/UserMessageDescriptors';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';

const COMMUNICATION_DESCRIPTOR = msg({
	message: 'Communication',
	comment: 'Short label in the privacy dashboard. Keep it concise.',
});

const DATA_EXPORT_DESCRIPTOR = msg({
	message: 'Data export',
	comment: 'Short label in the privacy dashboard. Keep it concise.',
});
const DATA_DELETION_DESCRIPTOR = msg({
	message: 'Data deletion',
	comment: 'Short label in the privacy dashboard. Keep it concise.',
});
export const PrivacyDashboardContent: React.FC = observer(() => {
	const {i18n} = useLingui();
	const accountKey = Accounts.currentAccountKey;
	const mutationController = useUserSettingsMutationController();
	return (
		<div
			key={accountKey}
			className={styles.container}
			data-flx="user.privacy-safety-tab.privacy-dashboard-content.container"
		>
			<SettingsSection
				id="profile-privacy"
				tabType="privacy_safety"
				title={i18n._(PROFILE_PRIVACY_DESCRIPTOR)}
				data-flx="user.privacy-safety-tab.privacy-dashboard-content.profile-privacy"
			>
				<ProfilePrivacyTab data-flx="user.privacy-safety-tab.privacy-dashboard-content.profile-privacy-tab" />
			</SettingsSection>
			{shouldShowFriendsAndDirectMessageSettings() && (
				<SettingsSection
					id="connections"
					tabType="privacy_safety"
					title={i18n._(getFriendsAndDirectMessagesSectionLabel())}
					data-flx="user.privacy-safety-tab.privacy-dashboard-content.connections"
				>
					<ConnectionsTab data-flx="user.privacy-safety-tab.privacy-dashboard-content.connections-tab" />
				</SettingsSection>
			)}
			{shouldShowDirectMessageSettings() && (
				<SettingsSection
					id="communication"
					tabType="privacy_safety"
					title={i18n._(COMMUNICATION_DESCRIPTOR)}
					data-flx="user.privacy-safety-tab.privacy-dashboard-content.communication"
				>
					<CommunicationTab
						mutationController={mutationController}
						data-flx="user.privacy-safety-tab.privacy-dashboard-content.communication-tab"
					/>
				</SettingsSection>
			)}
			{shouldShowFriendSettings() && (
				<SettingsSection
					id="active-now"
					tabType="privacy_safety"
					title={i18n._(ACTIVITY_SHARING_DESCRIPTOR)}
					data-flx="user.privacy-safety-tab.privacy-dashboard-content.active-now"
				>
					<ActiveNowTab data-flx="user.privacy-safety-tab.privacy-dashboard-content.active-now-tab" />
				</SettingsSection>
			)}
			<SettingsSection
				id="sensitive-content"
				tabType="privacy_safety"
				title={i18n._(SENSITIVE_CONTENT_DESCRIPTOR)}
				data-flx="user.privacy-safety-tab.privacy-dashboard-content.sensitive-content"
			>
				<SensitiveContentTab data-flx="user.privacy-safety-tab.privacy-dashboard-content.sensitive-content-tab" />
			</SettingsSection>
			<SettingsSection
				id="data-export"
				tabType="privacy_safety"
				title={i18n._(DATA_EXPORT_DESCRIPTOR)}
				data-flx="user.privacy-safety-tab.privacy-dashboard-content.data-export"
			>
				<DataExportTab data-flx="user.privacy-safety-tab.privacy-dashboard-content.data-export-tab" />
			</SettingsSection>
			<SettingsSection
				id="data-deletion"
				tabType="privacy_safety"
				title={i18n._(DATA_DELETION_DESCRIPTOR)}
				data-flx="user.privacy-safety-tab.privacy-dashboard-content.data-deletion"
			>
				<DataDeletionTab data-flx="user.privacy-safety-tab.privacy-dashboard-content.data-deletion-tab" />
			</SettingsSection>
		</div>
	);
});
